import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import localforage from 'localforage';
import { createLogger } from 'redux-logger';

// Import all slices
import authReducer from './slices/authSlice';
import uiReducer from './slices/uiSlice';
import patientsReducer from './slices/patientsSlice';
import reportsReducer from './slices/reportsSlice';
import loincReducer from './slices/loincSlice';
import dicomReducer from './slices/dicomSlice';
import adminReducer from './slices/adminSlice';

// Configure localforage for medical data
localforage.config({
  name: 'GynHubCloud',
  storeName: 'medicalData',
  description: 'Medical application data storage'
});

// Persist configuration aligned with existing medical patterns
const persistConfig = {
  key: 'gynhub-root',
  storage: localforage,
  whitelist: ['auth', 'ui', 'reports'], // Only persist critical state
  blacklist: ['patients', 'dicom'], // Don't persist sensitive medical data
};

const medicalPersistConfig = {
  key: 'gynhub-medical-drafts',
  storage: storage, // Use localStorage for drafts
  whitelist: ['draftData', 'recentTerms'],
};

// Root reducer combining all domain slices
const rootReducer = combineReducers({
  auth: authReducer,
  ui: uiReducer,
  patients: patientsReducer,
  reports: persistReducer(medicalPersistConfig, reportsReducer),
  loinc: loincReducer,
  dicom: dicomReducer,
  admin: adminReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure middleware for medical application
const getMiddleware = (getDefaultMiddleware: any) => {
  const middleware = getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: [
        'persist/PERSIST',
        'persist/REHYDRATE',
        'persist/PAUSE',
        'persist/PURGE',
        'persist/REGISTER',
      ],
      ignoredPaths: ['register', 'rehydrate'],
    },
    thunk: {
      extraArgument: {
        // Add extra arguments for medical APIs
        medicalApi: null, // Will be set up with React Query
      },
    },
  });

  // Add logger only in development
  if (process.env.NODE_ENV === 'development') {
    const logger = createLogger({
      predicate: (_getState: any, action: any) => {
        // Filter out noisy actions for medical debugging
        return !action.type.includes('persist/') && 
               !action.type.includes('@@INIT');
      },
      collapsed: true,
      diff: true,
    });
    middleware.push(logger);
  }

  return middleware;
};

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getMiddleware,
  devTools: {
    name: 'GynHub Cloud Medical System',
    features: {
      pause: true,
      lock: true,
      persist: true,
      export: true,
      import: 'custom',
      jump: true,
      skip: true,
      reorder: true,
      dispatch: true,
      test: true,
    },
    trace: true,
    traceLimit: 25,
  },
});

export const persistor = persistStore(store);

// Types for the complete medical state management
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Hooks for typed usage
export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectCurrentPatient = (state: RootState) => state.patients.currentPatient;
export const selectCurrentReport = (state: RootState) => state.reports.currentReport;
export const selectLoincLanguage = (state: RootState) => state.loinc.language;
export const selectUILanguage = (state: RootState) => state.ui.language;
